import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IconWeight } from '@widgets/eop-icon/eop-icon.component';

@Component({
  selector: 'eop-scroll-to-top',
  templateUrl: './scroll-to-top.component.html',
  styleUrls: ['./scroll-to-top.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ScrollToTopComponent implements OnInit, OnDestroy {
  tooltip = '';
  windowScrolled = false;
  readonly IconWeight = IconWeight;

  private readonly document = inject<Document>(DOCUMENT);
  private readonly translate = inject(TranslateService);
  private readonly renderer = inject(Renderer2);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);

  private readonly SCROLL_THRESHOLD = 600;
  private unlisten?: () => void;
  private scrollFrame?: number;

  ngOnInit(): void {
    this.tooltip = this.translate.instant('GLOBAL.SCROLL_TO_TOP');
    // Run the high-frequency scroll listener outside Angular to avoid triggering
    // change detection on every scroll event; only re-enter the zone when the
    // visibility state actually changes.
    this.zone.runOutsideAngular(() => {
      this.unlisten = this.renderer.listen(this.document.body, 'scroll', () => {
        this.onWindowScroll();
      });
    });
  }

  ngOnDestroy(): void {
    this.unlisten?.();
    if (this.scrollFrame !== undefined) {
      cancelAnimationFrame(this.scrollFrame);
    }
  }

  scrollToTop(): void {
    this.document.body.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }

  onWindowScroll(): void {
    // Throttle to at most one evaluation per animation frame.
    if (this.scrollFrame !== undefined) {
      return;
    }
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = undefined;
      const scrolled = this.document.body.scrollTop > this.SCROLL_THRESHOLD;
      if (scrolled !== this.windowScrolled) {
        this.zone.run(() => {
          this.windowScrolled = scrolled;
          this.cd.markForCheck();
        });
      }
    });
  }
}
